import { FC, useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Form as FormAnt, Input, ModalProps, Select, message } from "antd";
import { Modal } from '@hdi-seguros/web-components.modal';


import InsurancePolicyService from "../../../../../shared/services/insurancePolicy";
import { Combo, InsurancePolicy, PaymentMethod } from "../../../../../shared/interfaces";
import { Column, _renderColumns } from "../../utils/renderSections";

import { ColSize, RowSize, Form } from "../../../../../shared/components";
import { renderCurrency } from "../../../../../shared/utils";
import AutomaticDebitsRejectedService from "../../../../../shared/services/collection/automaticDebitsRejected";
import { PostChangePaymentTypeRequest } from "../../../../../shared/interfaces/services/change-payment-type-service";

import './index.css' 
import { PAYMENT_FORM_CODE } from "../../../../quotation/NewQuote/containers";

interface PaymentFormatModalProps extends ModalProps {
    insurancePolicy?: InsurancePolicy
    onClose: () => void;
    onConfirm: (paymentFormat: PaymentMethod) => void;
}

export const PaymentFormatModal: FC<PaymentFormatModalProps> = ({ open, title, onClose, insurancePolicy, onConfirm }) => {
    const [ paymentsTypes, setPaymentsTypes ] = useState<Array<Combo>>([]);
    const [creditCardsCompaniesCombo, setCreditCardsCompaniesCombo] = useState<Combo[]>([]);
    const [selectedPaymentType, setSelectedPaymentType] = useState<string>("");
 
    const [form] = FormAnt.useForm();
    const [loading, setLoading] = useState(false);

    let updatedPaymentMethod: PaymentMethod = insurancePolicy?.formaDePago!;

    const insuranceColumns: Array<Column> = useMemo(() => {
        const columns: Array<Column> = [
            {
                label: 'Rama',
                value: insurancePolicy?.descripcionRama
            },
            {
                label: 'Póliza',
                value: insurancePolicy?.nroPoliza,
            },
            {
                label: 'Vigencia desde',
                value: dayjs(insurancePolicy?.fechaVigDesd).format('DD/MM/YYYY'),
            },
            {
                label: 'Vigencia hasta',
                value: dayjs(insurancePolicy?.fechaVigHast).format('DD/MM/YYYY'),
            },
            {
                label: 'Asegurado',
                value: insurancePolicy?.nombreAsegurado
            },
            {
                label: 'Saldo',
                value: renderCurrency(insurancePolicy?.saldoPendiente!),
            },
            {
                label: 'Forma de pago actual',
                value: insurancePolicy?.formaDePago!.tipoDePago.descripcion,
            },
        ];

        // Agregar CBU o número de tarjeta según el tipo de pago
        const paymentTypeCode = insurancePolicy?.formaDePago?.tipoDePago?.codigo;
        const cbuNumber = insurancePolicy?.formaDePago?.numeroDeCbu;
        const creditCardNumber = insurancePolicy?.formaDePago?.numeroTarjetaDeCredito;

        if (paymentTypeCode === PAYMENT_FORM_CODE.TARJETA_CREDITO && creditCardNumber && creditCardNumber !== '' && creditCardNumber !== '0000000000000000000000000') {
            columns.push({
                label: 'Número de tarjeta de crédito',
                value: creditCardNumber,
            });
        } else if ((paymentTypeCode === PAYMENT_FORM_CODE.DEBITO_BANCARIO_CUENTA_CORRIENTE || paymentTypeCode === PAYMENT_FORM_CODE.DEBITO_BANCARIO_CAJA_AHORRO) && cbuNumber && cbuNumber !== '' && cbuNumber !== '0000000000000000000000000') {
            columns.push({
                label: 'Número de CBU',
                value: cbuNumber,
            });
        }

        return columns;
    }, [insurancePolicy]);

    const onSubmit = async () => {
        try {
            await form.validateFields();
            form.submit();
        } catch (error) { }
    };

    const onChangePaymentFormat = async () => {
        await postChangePaymentType()
    }

    const handleChange  = (value: string) => {
        setSelectedPaymentType(value)        
    } 

    const postChangePaymentType = async (): Promise<void> => {
        try {
            setLoading(true);
    
            const expirationDate: string = form.getFieldValue('expirationDate')?.replace("/", "") || "";
            const cardNumber: string = form.getFieldValue('cardNumber') || "";
            const cardTypeCode: string | number = form.getFieldValue('paymentCreditCardType') || "";
            const cbuNumber: string = form.getFieldValue('cbuNumber') || "";
    
            const request: PostChangePaymentTypeRequest = {
                articulo: insurancePolicy?.articulo!,
                superpoliza: insurancePolicy?.superpoliza!,
                rama: insurancePolicy?.rama!,
                poliza: insurancePolicy?.nroPoliza!,
                formaDePagoNueva: selectedPaymentType,
                info: '',
                vencimientoTarjetaDeCredito: ''
            };
    
            if (selectedPaymentType === PAYMENT_FORM_CODE.TARJETA_CREDITO) {
                const formattedCode: string = String(cardTypeCode).padStart(3, '0');
                request.info = formattedCode + cardNumber;
                request.vencimientoTarjetaDeCredito = expirationDate;
    
                updatedPaymentMethod.numeroTarjetaDeCredito = cardNumber;
                updatedPaymentMethod.fechaVencimientoTarjetaDeCredi = form.getFieldValue('expirationDate');
            } else if (selectedPaymentType === PAYMENT_FORM_CODE.DEBITO_BANCARIO_CUENTA_CORRIENTE || selectedPaymentType === PAYMENT_FORM_CODE.DEBITO_BANCARIO_CAJA_AHORRO) {
                request.info = cbuNumber;
                updatedPaymentMethod.numeroDeCbu = cbuNumber;
            }
            updatedPaymentMethod.tipoDePago.codigo = selectedPaymentType;
            updatedPaymentMethod.tipoDePago.descripcion = paymentsTypes.find(e => e.value === selectedPaymentType)?.label!;
            const response = await InsurancePolicyService.postChangePaymentFormat(request);
    
            if (response.status === 200) {
                message.success(`Se cambió la forma de pago de la póliza ${insurancePolicy?.nroPoliza}!`);                
                onConfirm(updatedPaymentMethod);
                onClose();
            }
        } catch (error) {} finally {
            setLoading(false);
        }
    };
    

    const getPaymentsTypes = async () => {
        try {
            setLoading(true);
            const response = await InsurancePolicyService.getPaymentsTypes({articulo: insurancePolicy?.articulo!, formaDePagoActual:insurancePolicy?.formaDePago.tipoDePago.codigo!});
            if( response?.formasDePago ) {
                
                let data = response?.formasDePago.map(({ tipoDePago }) => ({
                    label: tipoDePago.descripcion,
                    value: tipoDePago.codigo
                }));
                

                setPaymentsTypes( data );
            }
        } catch (error) {
        } finally {
            setLoading(false)
        }
    } 

    useEffect(() => {
        getPaymentsTypes();
        getCreditCardList();
    }, []);

    const getCreditCardList = useCallback(async () => {
        try {
            const response = await AutomaticDebitsRejectedService.getCreditCardList();            
            setCreditCardsCompaniesCombo(response.data);
        } catch (error) {
        } finally {
        }
    }, [setCreditCardsCompaniesCombo]);

    const getPaymentTypeDescription = (id:string) => {
        return paymentsTypes.find(e => e.value === id)
    }

    return (
        <Modal
            title={title}
            centered
            open={open}
            width={650}
            onCancel={onClose}
            onOk={onSubmit}
            confirmText={'Aceptar'}
            footer
            loadingConfirmButton={loading}
        >
            {
                <>
                    <RowSize rowProps={{ gutter: [0, 15], justify: 'start' }}>
                        {_renderColumns(insuranceColumns)}
                    </RowSize>

                    <Form
                        formProps={{
                            form,
                            name: "payment-format-form",
                            onFinish: onChangePaymentFormat,
                            style: { marginBottom: 10 },
                        }}
                    >
                        <RowSize rowProps={{ gutter: [0, 0] }}>
                            <ColSize size={100}>
                                <FormAnt.Item   
                                    className="payment-format-modal__form-select"
                                    label="Nueva forma de pago"
                                    name={'new-payment-type'}
                                    rules={[{ required: true, message: "Por favor selecciona una nueva forma de pago" }]}
                                >
                                    <Select options={paymentsTypes} onChange={handleChange} placeholder="Seleccione una forma de pago"/>
                                </FormAnt.Item>
                            </ColSize>
                            <ColSize className="payment-format-modal__form-inputs" size={100}>
                                {
                                    selectedPaymentType === PAYMENT_FORM_CODE.TARJETA_CREDITO ? 
                                        (
                                            <div>
                                                <FormAnt.Item
                                                    name="paymentCreditCardType"
                                                    label="Empresa tarjeta"
                                                    rules={[{ required: true, message: "Por favor selecciona un tipo de pago" }]}
                                                >
                                                    <Select
                                                        placeholder="Seleccione una empresa"
                                                        options={creditCardsCompaniesCombo}
                                                    />
                                                </FormAnt.Item>
                                                <FormAnt.Item
                                                    name="cardNumber"
                                                    label="Número de Tarjeta"
                                                    rules={[
                                                        { required: true, message: "El número de tarjeta es obligatorio" },
                                                        { pattern: /^\d+$/, message: "El campo solo puede contener números" },
                                                    ]}
                                                    >
                                                    <Input placeholder="Número de Tarjeta" maxLength={22} />
                                                </FormAnt.Item>
                                                <FormAnt.Item
                                                    name="expirationDate"
                                                    label="Fecha de expiración"
                                                    rules={[
                                                        { required: true, message: "La fecha de expiración es obligatoria" },
                                                        {
                                                            pattern: /^(0[1-9]|1[0-2])\/\d{2}$/,
                                                            message: "Formato inválido. Usa MM/YY",
                                                        },
                                                    ]}
                                                    >
                                                    <Input placeholder="Fecha de expiración" maxLength={5} />
                                                </FormAnt.Item>
                                            </div>
                                        ) : selectedPaymentType === PAYMENT_FORM_CODE.DEBITO_BANCARIO_CUENTA_CORRIENTE || selectedPaymentType === PAYMENT_FORM_CODE.DEBITO_BANCARIO_CAJA_AHORRO ?  (
                                            <FormAnt.Item
                                                name="cbuNumber"
                                                label="Número de CBU"
                                                rules={[
                                                    { required: true, message: "El CBU es obligatorio" },
                                                    {
                                                    len: 22,
                                                    message: "El CBU debe tener exactamente 22 caracteres",
                                                    },
                                                ]}
                                                >
                                                <Input placeholder="Número de CBU" maxLength={22} />
                                            </FormAnt.Item>
                                        ) : (
                                            null
                                        )
                                }
                            </ColSize>
                        </RowSize>
                    </Form>
                </>
            }
        </Modal>
    )
}
